import { useMemo, useState } from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  Badge,
  DataTable,
  EmptyState,
  PageBody,
  StatCard,
  type DataTableColumn,
} from '@wundo/ui';
import { PageHeader } from '@/components/PageHeader.tsx';
import { OutreachDetailDialog } from '@/components/admin/outreach-queue/OutreachDetailDialog.tsx';
import { OutreachFilters } from '@/components/admin/outreach-queue/OutreachFilters.tsx';
import {
  EMPTY_OUTREACH_FILTERS,
  type OutreachFilterState,
} from '@/components/admin/outreach-queue/outreachFilterState.ts';
import {
  formatFireTime,
  kindLabel,
  kindTone,
  outreachUserName,
  payloadExcerpt,
  relativeFromNow,
} from '@/components/admin/outreach-queue/outreachFormat.ts';
import { useOrganisationFilter } from '@/lib/activeOrganisation.ts';
import { useResetOnChange } from '@/lib/useResetOnChange.ts';
import {
  fetchOutreachQueue,
  type OutreachQueueEntry,
  type OutreachQueueFilters,
} from '@/lib/outreachQueue.ts';

export const Route = createFileRoute('/_app/admin/outreach-queue')({
  staticData: { orgScope: 'optional' },
  component: OutreachQueuePage,
});

/**
 * What the skill engines are about to send, before they send it.
 *
 * Not a list of records but a look ahead: everything is ordered by when it
 * fires, and every instant is written in the recipient's timezone at their own
 * notification hour, because that is the question the page exists to answer.
 */
export function OutreachQueuePage() {
  const [filters, setFilters] = useState<OutreachFilterState>(EMPTY_OUTREACH_FILTERS);
  const [detail, setDetail] = useState<OutreachQueueEntry | null>(null);
  const organisationId = useOrganisationFilter()?.id;

  /*
   * A person belongs to one organisation: a name left from the previous one would
   * narrow the list to nothing and look like an empty queue.
   */
  useResetOnChange(organisationId, () =>
    setFilters((current) => ({ ...current, user: null })),
  );

  const query = useMemo<OutreachQueueFilters>(
    () => ({
      user_id: filters.user?.id,
      organisation_id: organisationId,
      skill_id: filters.skillId || undefined,
      kind: filters.kind || undefined,
      window_hours: filters.windowHours,
      include_expired: filters.includeExpired,
    }),
    [filters, organisationId],
  );

  const entries = useQuery({
    queryKey: ['outreachQueue', query],
    queryFn: ({ signal }) => fetchOutreachQueue(query, { signal }),
    placeholderData: keepPreviousData,
    throwOnError: true,
  });

  const rows = useMemo(() => entries.data ?? [], [entries.data]);

  const counts = useMemo(() => {
    const result = { recommendation: 0, reminder: 0, check_in: 0 };
    for (const entry of rows) result[entry.kind]++;
    return result;
  }, [rows]);

  const columns = useMemo<DataTableColumn<OutreachQueueEntry>[]>(
    () => [
      {
        key: 'fire_at',
        header: 'When',
        width: 'w-52',
        render: (entry) => (
          <span className='block whitespace-nowrap'>
            <span className='block font-semibold text-ink'>
              {relativeFromNow(entry.fire_at)}
            </span>
            <span className='block text-xs text-ink-muted'>
              {formatFireTime(entry.fire_at, entry.user.timezone)} ({entry.user.timezone})
            </span>
          </span>
        ),
      },
      {
        key: 'kind',
        header: 'Kind',
        width: 'w-32',
        render: (entry) => (
          <Badge tone={kindTone(entry.kind)}>{kindLabel(entry.kind)}</Badge>
        ),
      },
      {
        key: 'skill',
        header: 'Skill',
        render: (entry) =>
          entry.skill ? (
            <Link
              to='/skills/$id/edit'
              params={{ id: entry.skill.id }}
              className='font-semibold text-ink hover:underline'
            >
              {entry.skill.name}
            </Link>
          ) : (
            <span className='text-ink-muted'>—</span>
          ),
      },
      {
        key: 'user',
        header: 'User',
        render: (entry) => (
          <Link
            to='/users/$id'
            params={{ id: entry.user.id }}
            hash='skills'
            className='text-ink'
          >
            {outreachUserName(entry.user)}
          </Link>
        ),
      },
      {
        key: 'organisation',
        header: 'Organisation',
        render: (entry) => entry.organisation.name,
      },
      {
        /* No width: the reason is what the row is about, so it takes the slack. */
        key: 'payload',
        header: 'Payload',
        render: (entry) => (
          <span className='line-clamp-2 text-ink-secondary'>{payloadExcerpt(entry)}</span>
        ),
      },
      {
        key: 'priority',
        header: 'Pri',
        width: 'w-12',
        render: (entry) => (
          <span className='font-mono text-ink-muted'>{entry.priority}</span>
        ),
      },
    ],
    [],
  );

  return (
    <>
      <PageHeader title='Outreach queue' />
      <PageBody className='flex flex-col gap-5'>
        <p className='text-sm text-ink-muted'>
          Skill-related tasks waiting to fire: recommendations, reminders, and check-ins.
          Times are shown in each user&apos;s local timezone, at their notification time.
          Default window is the next 48 hours.
        </p>

        <OutreachFilters
          organisationId={organisationId}
          value={filters}
          onChange={setFilters}
        />

        <div className='grid grid-cols-4 gap-3 max-mobile:grid-cols-2'>
          <StatCard label='Total' value={rows.length} />
          <StatCard label='Recs' value={counts.recommendation} />
          <StatCard label='Reminders' value={counts.reminder} />
          <StatCard label='Check-ins' value={counts.check_in} />
        </div>

        <DataTable<OutreachQueueEntry>
          label='Outreach queue'
          columns={columns}
          rows={rows}
          rowKey={(entry) => entry.task_id}
          loading={entries.isFetching}
          onRowClick={setDetail}
          emptyState={
            <EmptyState
              title='Nothing scheduled in this window'
              description='Widen the window, or clear a filter.'
            />
          }
        />
      </PageBody>

      <OutreachDetailDialog entry={detail} onClose={() => setDetail(null)} />
    </>
  );
}
